import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useServices } from "./ServicesContext";
import AddProductModal from "./components/AddProductModal";
import EditProductModal from "./components/EditProductModal";
import EditServiceModal from "./components/EditServiceModal";
import Pagination from "components/pagination";
import { uploadImage } from "utils/api";
import { MdArrowBack } from "react-icons/md";


/* ----------------------------
   Currency formatter (INR by default)
   ---------------------------- */
const formatCurrency = (
  value,
  {
    currency = "INR",
    locale = (typeof navigator !== "undefined" && navigator.language) || "en-IN",
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = {}
) => {
  const num =
    typeof value === "string" ? Number(value.toString().replace(/,/g, "")) : Number(value);
  if (!isFinite(num)) return "";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(num);
  } catch {
    return `${currency} ${num.toFixed(2)}`;
  }
};

const ServiceDetails = () => {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    services,
    addProduct,
    editProduct,
    removeProduct,
    deleteService,
    toggleServiceStatus,
    editService,
    addAddon,
    editAddon,
    removeAddon,
  } = useServices();

  const service = services.find((s) => s.id === serviceId);

  const [showAddProductModal, setShowAddProductModal] = useState(
    Boolean(location.state?.openAdd)
  );
  const [newProduct, setNewProduct] = useState({
    name: "",
    fee: "",
    image: "",
    status: "active",
  });

  // Addon modal state
  const [showAddAddonModal, setShowAddAddonModal] = useState(false);
  const [newAddon, setNewAddon] = useState({ name: "", fee: "", image: "" });
  const addonFileInputRef = useRef(null);
  const [addonUploading, setAddonUploading] = useState(false);


  const [showEditServiceModal, setShowEditServiceModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [editAddonForm, setEditAddonForm] = useState({ name: "", fee: "", image: "" });
  const editAddonFileInputRef = useRef(null);
  const [editAddonUploading, setEditAddonUploading] = useState(false);

  const PRODUCTS_PAGE_SIZE = 10;
  const ADDONS_PAGE_SIZE = 10;
  const [productsPage, setProductsPage] = useState(1);
  const [addonsPage, setAddonsPage] = useState(1);

  const products = service?.products ?? [];
  const addons = service?.addons ?? [];
  const productsTotalPages = Math.max(1, Math.ceil(products.length / PRODUCTS_PAGE_SIZE));
  const addonsTotalPages = Math.max(1, Math.ceil(addons.length / ADDONS_PAGE_SIZE));
  const paginatedProducts = products.slice(
    (productsPage - 1) * PRODUCTS_PAGE_SIZE,
    productsPage * PRODUCTS_PAGE_SIZE
  );
  const paginatedAddons = addons.slice(
    (addonsPage - 1) * ADDONS_PAGE_SIZE,
    addonsPage * ADDONS_PAGE_SIZE
  );

  useEffect(() => {
    setProductsPage(1);
    setAddonsPage(1);
  }, [serviceId]);

  useEffect(() => {
    if (!service) {
      toast.error("Service not found");
      navigate("/admin/services");
    }
  }, [service, navigate]);

  if (!service) return null;

  const handleSaveProduct = () => {
    addProduct(service.id, newProduct);
    setShowAddProductModal(false);
    setNewProduct({ name: "", fee: "", image: "", status: "active" });
  };

  const handleDeleteService = () => {
    deleteService(service.id);
    navigate("/admin/services");
  };

  const handleSaveAddon = () => {
    addAddon(service.id, newAddon);
    setShowAddAddonModal(false);
    setNewAddon({ name: "", fee: "", image: "" });
  };

  const handleSaveEditProduct = (updates) => {
    if (!editingProduct) return;
    editProduct(editingProduct.id, updates);
    setEditingProduct(null);
  };

  const handleSaveEditAddon = (updates) => {
    if (!editingAddon) return;
    editAddon(editingAddon.id, updates);
    setEditingAddon(null);
    setEditAddonForm({ name: "", fee: "", image: "" });
  };

  const handleEditAddonFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (e.g. JPG, PNG)");
      return;
    }
    setEditAddonUploading(true);
    try {
      const res = await uploadImage(file, "addons");
      const url = res.data?.url;
      if (url) {
        setEditAddonForm((f) => ({ ...f, image: url }));
        toast.success("Image uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      console.error(err);
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d?.msg;
      toast.error(msg || "Failed to upload image");
    } finally {
      setEditAddonUploading(false);
      if (editAddonFileInputRef.current) editAddonFileInputRef.current.value = "";
    }
  };

  const handleAddonFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (e.g. JPG, PNG)");
      return;
    }
    setAddonUploading(true);
    try {
      const res = await uploadImage(file, "addons");
      const url = res.data?.url;
      if (url) {
        setNewAddon({ ...newAddon, image: url });
        toast.success("Image uploaded");
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      console.error(err);
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d : Array.isArray(d) ? d.map((x) => x.msg).join(" ") : d?.msg;
      toast.error(msg || "Failed to upload image");
    } finally {
      setAddonUploading(false);
      if (addonFileInputRef.current) addonFileInputRef.current.value = "";
    }
  };

  const handleSaveEditService = async (payload) => {
    const result = await editService({ id: service.id, ...payload });
    if (result !== null) setShowEditServiceModal(false);
  };

  return (
    <div className="w-full min-h-screen bg-white dark:bg-navy-900">
      {/* Back */}
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/admin/services")}
          className="ml-4 flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50"
          aria-label="Back to Runner Boys"
        >
          <MdArrowBack className="h-5 w-5" />
        </button>
        <div>
          <h1 className="mt-4 text-xl font-bold text-gray-900 sm:text-2xl">
            Service
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Overview of service information
          </p>
        </div>
      </div>

      {/* Header */}
      <header className="mt-4 w-full bg-white text-gray-900 shadow-md">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">

          {/* Service Info */}
          <div className="flex-1 min-w-0">
            {service.banner_img && (
              <div className="mb-4 rounded-lg overflow-hidden w-32 h-24 shadow-sm">
                <img src={service.banner_img} alt={service.name} className="w-full h-full object-cover" />
              </div>
            )}
            <h1 className="text-3xl font-bold tracking-tight">{service.name}</h1>
            <p className="text-gray-500 text-sm mt-1">
              Service Details · ID: {service.id}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Edit Button */}
            <button
              onClick={() => setShowEditServiceModal(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-colors bg-brand-500 hover:bg-brand-600 text-white border border-transparent"
              title="Edit service name and image"
            >
              Edit Service
            </button>

            {/* Status Toggle */}
            <button
              onClick={() => toggleServiceStatus(service.id)}
              className={`px-4 py-2 text-sm font-medium rounded-lg shadow-sm transition-colors border border-transparent 
          ${service.status === "active"
                  ? "bg-green-500 hover:bg-green-600 text-white"
                  : "bg-red-500 hover:bg-red-600 text-white"
                }`}
              title="Toggle Service Status"
            >
              {service.status === "active" ? "Active" : "Inactive"}
            </button>

            {/* Delete Button */}
            <button
              onClick={handleDeleteService}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-red-500 hover:bg-red-600 text-white shadow-sm transition-colors"
              title="Delete Service"
            >
              Delete Service
            </button>
          </div>
        </div>
      </header>

      {showEditServiceModal && (
        <EditServiceModal
          service={service}
          onCancel={() => setShowEditServiceModal(false)}
          onSave={handleSaveEditService}
        />
      )}

      {/* (Optional) Meta – if you want to show more details, you can reintroduce a meta grid here */}

      {/* Products: TABLE VIEW (Professional + Readable, No Edit) */}
      {/* Products: TABLE VIEW (White BG, Professional + Readable, No Edit) */}
      <section className="w-full bg-white dark:bg-navy-900 border-t border-gray-200 dark:border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-navy-800 dark:text-white">
              Products ({service.products.length})
            </h2>
            <button
              onClick={() => setShowAddProductModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 
        text-white rounded-lg font-semibold shadow transition"
            >
              <span className="text-lg leading-none">＋</span>
              Add Product
            </button>
          </div>

          {service.products.length === 0 ? (
            <div className="w-full py-12 text-center rounded-lg border-2 border-dashed border-gray-300 dark:border-white/10 text-gray-600 dark:text-gray-400 bg-white dark:bg-navy-900">
              No products yet. Click <strong>“Add Product”</strong> to add one.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-900 shadow-sm">
              <table className="min-w-[720px] w-full text-sm">
                {/* Sticky header with white bg */}
                <thead className="sticky top-0 z-10 bg-brand-600 text-white dark:bg-brand-500/90 backdrop-blur-sm">
                  <tr className="text-left text-xs uppercase tracking-wide">
                    <th className="px-5 py-3 font-bold w-[18%]">ID</th>
                    <th className="px-5 py-3 font-bold w-[34%]">Name</th>
                    <th className="px-5 py-3 font-bold text-right w-[18%]">Fee</th>
                    <th className="px-5 py-3 font-bold w-[16%]">Status</th>
                    <th className="px-5 py-3 font-bold text-right w-[14%]">Actions</th>
                  </tr>
                </thead>

                {/* Striped rows + hover highlight */}
                <tbody className="divide-y divide-gray-200 font-bold dark:divide-white/10">
                  {paginatedProducts.map((product, idx) => (
                    <tr
                      key={product.id}
                      className={`transition hover:bg-gray-100  dark:hover:bg-white/5 ${idx % 2 === 1 ? "bg-gray-50/40 dark:bg-white/[0.03]" : ""
                        }`}
                    >
                      {/* ID: mono, wrap-safe */}
                      <td className="px-5 py-3 align-middle">
                        <span className="text-xs text-gray-700 dark:text-gray-300 font-mono break-all">
                          {product.id}
                        </span>
                      </td>

                      {/* Name: truncate with tooltip */}
                      <td className="px-5 py-3 align-middle">
                        <div className="max-w-[360px]">
                          <span
                            className="font-medium text-navy-800 dark:text-white truncate block"
                            title={product.name}
                          >
                            {product.name}
                          </span>
                        </div>
                      </td>

                      {/* Fee: right-aligned, currency formatted */}
                      <td className="px-5 py-3 align-middle text-right whitespace-nowrap">
                        <span className="font-semibold text-green-700 dark:text-green-300 tabular-nums">
                          {formatCurrency(product.fee)}
                        </span>
                      </td>

                      {/* Status: badge with dot */}
                      <td className="px-5 py-3 align-middle">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold
                      ${product.status === "active"
                              ? "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200"
                              : "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200"
                            }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${product.status === "active" ? "bg-green-600" : "bg-red-600"
                              }`}
                          />
                          {product.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>

                      {/* Actions: Edit + Remove */}
                      <td className="px-5 py-3 align-middle text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => setEditingProduct(product)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-white bg-blue-500 hover:bg-blue-600 transition shadow-sm text-xs font-semibold"
                            title="Edit"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeProduct(service.id, product.id)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-white bg-red-500 hover:bg-red-600 transition shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-red-500/60 focus-visible:ring-offset-white dark:focus-visible:ring-offset-navy-900"
                            title="Remove"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M9 3a1 1 0 00-1 1v1H4a1 1 0 100 2h.293l1.2 12.004A3 3 0 008.483 22h7.034a3 3 0 002.99-2.996L19.707 7H20a1 1 0 100-2h-4V4a1 1 0 00-1-1H9zm2 5a1 1 0 112 0v9a1 1 0 11-2 0V8zM8 8a1 1 0 112 0v9a1 1 0 11-2 0V8zm6 0a1 1 0 112 0v9a1 1 0 11-2 0V8z" />
                            </svg>
                            <span className="hidden sm:inline text-xs">Remove</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                currentPage={productsPage}
                totalPages={productsTotalPages}
                totalItems={products.length}
                pageSize={PRODUCTS_PAGE_SIZE}
                onPageChange={setProductsPage}
                itemName="products"
              />
            </div>
          )}
        </div>
      </section>
      {/* Addon Services Section */}
      <section className="w-full bg-white dark:bg-navy-900 border-t border-gray-200 dark:border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-navy-800 dark:text-white">
              Addon Services ({(service.addons || []).length})
            </h2>
            <button
              onClick={() => setShowAddAddonModal(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-semibold shadow transition"
            >
              <span className="text-lg leading-none">＋</span>
              Add Addon
            </button>
          </div>

          {(service.addons || []).length === 0 ? (
            <div className="w-full py-12 text-center rounded-lg border-2 border-dashed border-gray-300 dark:border-white/10 text-gray-600 dark:text-gray-400 bg-white dark:bg-navy-900">
              No addon services yet. Click <strong>"Add Addon"</strong> to add optional extras like Perfume Spray, Starch, etc.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-navy-900 shadow-sm">
              <table className="min-w-[600px] w-full text-sm">
                <thead className="sticky top-0 z-10 bg-purple-600 text-white dark:bg-purple-500/90 backdrop-blur-sm">
                  <tr className="text-left text-xs uppercase tracking-wide">
                    <th className="px-5 py-3 font-bold w-[40%]">Name</th>
                    <th className="px-5 py-3 font-bold text-right w-[20%]">Fee</th>
                    <th className="px-5 py-3 font-bold w-[20%]">Status</th>
                    <th className="px-5 py-3 font-bold text-right w-[20%]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                  {paginatedAddons.map((addon, idx) => (
                    <tr
                      key={addon.id}
                      className={`transition hover:bg-gray-100 dark:hover:bg-white/5 ${idx % 2 === 1 ? "bg-gray-50/40 dark:bg-white/[0.03]" : ""
                        }`}
                    >
                      <td className="px-5 py-3 align-middle font-medium text-navy-800 dark:text-white">
                        {addon.name}
                      </td>
                      <td className="px-5 py-3 align-middle text-right whitespace-nowrap">
                        <span className="font-semibold text-purple-700 dark:text-purple-300 tabular-nums">
                          {formatCurrency(addon.fee)}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-middle">
                        <span
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${addon.status === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-200"
                            : "bg-red-100 text-red-800 dark:bg-red-500/20 dark:text-red-200"
                            }`}
                        >
                          <span
                            className={`h-1.5 w-1.5 rounded-full ${addon.status === "active" ? "bg-green-600" : "bg-red-600"
                              }`}
                          />
                          {addon.status === "active" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-5 py-3 align-middle text-right">
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingAddon(addon);
                              setEditAddonForm({ name: addon.name, fee: addon.fee, image: addon.image || addon.banner_img || "" });
                            }}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-white bg-blue-500 hover:bg-blue-600 transition shadow-sm text-xs font-semibold"
                            title="Edit addon"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => removeAddon(service.id, addon.id)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-white bg-red-500 hover:bg-red-600 transition shadow-sm text-xs font-semibold"
                            title="Remove addon"
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Pagination
                currentPage={addonsPage}
                totalPages={addonsTotalPages}
                totalItems={addons.length}
                pageSize={ADDONS_PAGE_SIZE}
                onPageChange={setAddonsPage}
                itemName="addons"
              />
            </div>
          )}
        </div>
      </section>

      {/* Add Product Modal */}
      {showAddProductModal && (
        <AddProductModal
          service={service}
          newProduct={newProduct}
          setNewProduct={setNewProduct}
          onCancel={() => setShowAddProductModal(false)}
          onSave={handleSaveProduct}
        />
      )}

      {/* Edit Product Modal */}
      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          service={service}
          onCancel={() => setEditingProduct(null)}
          onSave={handleSaveEditProduct}
        />
      )}

      {/* Add Addon Modal */}
      {showAddAddonModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold mb-4 text-navy-700">
              Add Addon to "{service.name}"
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Addon Name</label>
                <input
                  type="text"
                  value={newAddon.name}
                  onChange={(e) => setNewAddon({ ...newAddon, name: e.target.value })}
                  placeholder="e.g. Perfume Spray"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Fee (INR)</label>
                <input
                  type="number"
                  value={newAddon.fee}
                  onChange={(e) => setNewAddon({ ...newAddon, fee: e.target.value })}
                  placeholder="e.g. 20"
                  className="w-full rounded-lg border px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Addon Image</label>
                <input
                  ref={addonFileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleAddonFileChange}
                  className="hidden"
                />
                {newAddon.image ? (
                  <div className="relative rounded-lg border-2 border-gray-200 dark:border-white/10 overflow-hidden bg-gray-100 dark:bg-navy-800">
                    <img
                      src={newAddon.image}
                      alt="Addon"
                      className="w-full h-40 object-cover"
                    />
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={() => addonFileInputRef.current?.click()}
                        disabled={addonUploading}
                        className="px-3 py-1.5 bg-white/90 text-navy-700 rounded-md text-sm font-semibold disabled:opacity-50"
                      >
                        {addonUploading ? "Uploading…" : "Change"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setNewAddon({ ...newAddon, image: "" })}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-md text-sm font-semibold"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => addonFileInputRef.current?.click()}
                    disabled={addonUploading}
                    className="w-full px-4 py-6 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg text-gray-500 dark:text-gray-400 hover:border-purple-500 hover:text-purple-600 transition-colors flex flex-col items-center gap-2"
                  >
                    {addonUploading ? (
                      <span className="font-medium">Uploading…</span>
                    ) : (
                      <>
                        <span className="text-2xl">📷</span>
                        <span className="font-medium">Choose image (optional)</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowAddAddonModal(false);
                  setNewAddon({ name: "", fee: "", image: "" });
                }}
                className="rounded-lg border px-4 py-2 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAddon}
                className="rounded-lg bg-purple-600 hover:bg-purple-700 px-4 py-2 text-sm text-white font-semibold"
              >
                Save Addon
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Addon Modal */}
      {editingAddon && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-500/50 p-4">
          <div className="w-full max-w-md rounded-xl bg-white dark:bg-navy-800 p-6 shadow-xl border border-gray-200 dark:border-white/10">
            <h3 className="text-lg font-bold mb-4 text-navy-700 dark:text-white">
              Edit Addon
            </h3>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Addon Name</label>
                <input
                  type="text"
                  value={editAddonForm.name}
                  onChange={(e) => setEditAddonForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Perfume Spray"
                  className="w-full rounded-lg border px-3 py-2 text-sm dark:bg-navy-900 dark:border-white/10 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Fee (INR)</label>
                <input
                  type="number"
                  value={editAddonForm.fee}
                  onChange={(e) => setEditAddonForm((f) => ({ ...f, fee: e.target.value }))}
                  placeholder="e.g. 20"
                  className="w-full rounded-lg border px-3 py-2 text-sm dark:bg-navy-900 dark:border-white/10 dark:text-white"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Addon Image</label>
                <input ref={editAddonFileInputRef} type="file" accept="image/*" onChange={handleEditAddonFileChange} className="hidden" />
                {editAddonForm.image ? (
                  <div className="relative rounded-lg border-2 border-gray-200 dark:border-white/10 overflow-hidden bg-gray-100 dark:bg-navy-800">
                    <img src={editAddonForm.image} alt="Addon" className="w-full h-40 object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => editAddonFileInputRef.current?.click()} disabled={editAddonUploading} className="px-3 py-1.5 bg-white/90 text-navy-700 rounded-md text-sm font-semibold disabled:opacity-50">Change</button>
                      <button type="button" onClick={() => setEditAddonForm((f) => ({ ...f, image: "" }))} className="px-3 py-1.5 bg-red-500 text-white rounded-md text-sm font-semibold">Remove</button>
                    </div>
                  </div>
                ) : (
                  <button type="button" onClick={() => editAddonFileInputRef.current?.click()} disabled={editAddonUploading} className="w-full px-4 py-6 border-2 border-dashed border-gray-300 dark:border-white/20 rounded-lg text-gray-500 dark:text-gray-400 hover:border-purple-500 flex flex-col items-center gap-2">
                    {editAddonUploading ? <span className="font-medium">Uploading…</span> : (<>📷 <span className="font-medium">Upload image (optional)</span></>)}
                  </button>
                )}
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => { setEditingAddon(null); setEditAddonForm({ name: "", fee: "", image: "" }); }} className="rounded-lg border px-4 py-2 text-sm dark:border-white/20 dark:text-white">
                Cancel
              </button>
              <button onClick={() => handleSaveEditAddon(editAddonForm)} className="rounded-lg bg-purple-600 hover:bg-purple-700 px-4 py-2 text-sm text-white font-semibold">
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetails;